import React from "react";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Privacy Policy
          </h1>

          <div className="text-sm text-gray-600 mb-8 text-center">
            <strong>Effective Date: January 10, 2025</strong>
          </div>

          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                1. Purpose of This Privacy Policy
              </h2>
              <p className="text-gray-700 leading-relaxed">
                This Privacy Policy explains how we collect, use, protect, and
                share the information you provide when using our website,
                platform, or services. It applies to all users who access or
                interact with our platform and covers all personal data shared
                with us while using our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                2. Data We Collect
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                When you use our platform, we may collect and process the
                following information:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>
                  <strong>Personal details:</strong> Name, email, phone number,
                  address, gender, etc. (provided during registration, account
                  creation, or form submissions).
                </li>
                <li>
                  <strong>Account information:</strong> Login credentials, user
                  IDs, and password details.
                </li>
                <li>
                  <strong>Financial details:</strong> Payment information for
                  subscriptions or services.
                </li>
                <li>
                  <strong>Social media data:</strong> If you log in via Facebook
                  or other platforms, we may access your basic profile
                  information with your consent.
                </li>
                <li>
                  <strong>Communications:</strong> Emails, messages, and other
                  correspondence between you and us.
                </li>
                <li>
                  <strong>Content:</strong> Any material or information you
                  upload, submit, or post on our platform.
                </li>
                <li>
                  <strong>Technical data:</strong> IP address, browser type,
                  device specifications, and operating system details.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                3. How We Use Your Data
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We collect your information only for lawful and legitimate
                purposes, including:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Verifying your identity when signing in.</li>
                <li>Managing your user account and activity.</li>
                <li>
                  Analyzing platform performance (e.g., number of users, page
                  visits) without identifying individuals.
                </li>
                <li>
                  Contacting you in response to queries or for service-related
                  matters.
                </li>
                <li>
                  Providing marketing or promotional updates about our services
                  (unless you opt out).
                </li>
              </ul>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4">
                <p className="text-blue-800">
                  <strong>Note:</strong> We do not use your personal data for
                  purposes other than providing you access to your account,
                  services, and the platform. Payments are processed securely
                  via trusted third-party gateways.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                4. Sharing and Disclosure
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may share your personal information only in these cases:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>
                  <strong>Affiliates and partners:</strong> With group
                  companies, subsidiaries, or partners who comply with this
                  Privacy Policy.
                </li>
                <li>
                  <strong>Third-party providers:</strong> With service providers
                  (e.g., payment processors, cloud hosting, email services)
                  necessary for delivering our services.
                </li>
                <li>
                  <strong>Legal requirements:</strong> If required by law, court
                  orders, or government requests, or to protect rights, safety,
                  or property.
                </li>
                <li>
                  <strong>Business changes:</strong> In case of merger,
                  acquisition, or restructuring, your data may be transferred as
                  part of business assets.
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                We do not allow service providers to use your data for their own
                marketing or unrelated purposes.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                5. Data Security
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We use industry-standard 256-bit SSL encryption and other
                safeguards to protect your data during transmission. While no
                method is 100% secure, we take reasonable steps to protect your
                personal information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                6. Data Transfers
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Your data may be stored or processed outside your home country.
                By using our services, you consent to such transfers, which may
                be subject to the local laws of those jurisdictions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                7. Data Retention
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We retain your personal data only while your account is active
                and as required by law. After account termination, we delete or
                anonymize data unless retention is legally necessary.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                8. Your Rights
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Depending on applicable laws (e.g., Indian IT Rules 2011), you
                may:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Access, review, or correct your data.</li>
                <li>Request deletion of your personal information.</li>
                <li>
                  Withdraw consent to processing (this may limit your use of our
                  services).
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                To make a request, email{" "}
                <a
                  href="mailto:support@educationplanner.io"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  support@educationplanner.io
                </a>
                .
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                9. Children's Privacy
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Our services are not intended for children under 16. We do not
                knowingly collect data from minors. If you believe we have
                collected such data, contact{" "}
                <a
                  href="mailto:support@educationplanner.io"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  support@educationplanner.io
                </a>{" "}
                and we will remove it promptly.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                10. Spam Policy
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We oppose spam. We email only users who subscribe to
                updates/newsletters or submit forms. You can unsubscribe at any
                time using the link in our emails or by emailing
                <a
                  href="mailto:support@educationplanner.io"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  {" "}
                  support@educationplanner.io
                </a>
                .
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                11. Third-Party Links
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Our website may link to external sites. We are not responsible
                for their privacy practices. Please review their policies before
                sharing personal data.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                12. Changes to This Policy
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy periodically. Any changes will
                be posted here with an updated effective date. Continued use of
                our services indicates acceptance of the updated terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                13. Contact Us
              </h2>
              <p className="text-gray-700 leading-relaxed">
                For questions, concerns, or privacy requests, contact:
              </p>
              <p className="text-gray-700 leading-relaxed mt-2">
                📧{" "}
                <a
                  href="mailto:support@educationplanner.io"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  support@educationplanner.io
                </a>
              </p>
            </section>

            <div className="border-t border-gray-200 pt-8 mt-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Copyright Statement
              </h2>
              <p className="text-gray-700 leading-relaxed">
                This website and its content are the copyright of Education
                Planner — © 2025. All rights are reserved. Any reproduction,
                redistribution, restructuring, or posting of part or all of the
                content in any form is strictly prohibited without prior written
                permission. You may not distribute or commercially exploit the
                content, nor may you transmit it or store it in any other
                website or any other form of electronic retrieval system without
                our written consent.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
